import { PulseLoader } from "@/components/pulse_loader";
import { getMaxCapacity, Space } from "@/data";
import { useGetPartnerOffering } from "@/data/api";
import {
  PartnerOffering,
  PartnerOfferingLocation,
  SortOrder,
} from "@/data/partner_offering";
import { useMediaQuery } from "@/hooks";
import { toDay } from "@/lib/datetime";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { useSpacesWithLoadingQuery } from "@/offering/api";
import { MeetingRoomsFilter } from "@/offering/template_offering_listing_meeting_room";
import classNames from "classnames";
import { addDays, subDays } from "date-fns";
import { uniq, uniqBy } from "lodash";
import React, { useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { RoomGroup } from "../templates/template_offering_listing_gallery";
import { calculateDistance } from "../utils/calculate_distance";

interface SimilarRoomsProps {
  filter: MeetingRoomsFilter;
  partnerOfferingId: string;
  sortOrder?: SortOrder;
}

// in meter
const NEARBY_LOCATIONS_DISTANCE = 40000;

const DEFAULT_NUMBER_OF_DISPLAYED_ROOMS = 6;

export function SimilarRooms({
  filter,
  partnerOfferingId,
  sortOrder,
}: SimilarRoomsProps) {
  const { fbt } = useI18n();
  const { device } = useMediaQuery();
  const isMobile = device === "mobile";

  const partnerOffering = useGetPartnerOffering(partnerOfferingId);

  const partnerOfferingWithMinCapacity = useGetPartnerOffering(
    partnerOfferingId,
    { ...filter, maxCapacity: undefined }
  );
  const minCapacitySpaces = useSpacesWithLoadingQuery(
    partnerOfferingWithMinCapacity.offeringLocations.map((l) => l.spaceID)
  );

  const partnerOfferingWitSameDate = useGetPartnerOffering(partnerOfferingId, {
    ...filter,
    startTime: undefined,
    endTime: undefined,
  });
  const sameDateSpaces = useSpacesWithLoadingQuery(
    partnerOfferingWitSameDate.offeringLocations.map((l) => l.spaceID)
  );
  const partnerOfferingWithPreviousDate = useGetPartnerOffering(
    partnerOfferingId,
    {
      ...filter,
      date: filter.date && toDay(subDays(new Date(filter.date), 1)),
      startTime: undefined,
      endTime: undefined,
    }
  );
  const previousDateSpaces = useSpacesWithLoadingQuery(
    partnerOfferingWithPreviousDate.offeringLocations.map((l) => l.spaceID)
  );
  const partnerOfferingWithNextDate = useGetPartnerOffering(partnerOfferingId, {
    ...filter,
    date: filter.date && toDay(addDays(new Date(filter.date), 1)),
    startTime: undefined,
    endTime: undefined,
  });
  const nextDateSpaces = useSpacesWithLoadingQuery(
    partnerOfferingWithNextDate.offeringLocations.map((l) => l.spaceID)
  );

  const nearbyLocationIds = useMemo(() => {
    const citiesOfSelectedLocations = uniq(
      partnerOffering.offeringLocations
        .filter((l) => filter.locationIDs?.includes(l.location.id))
        .map((l) => l.location.city)
    );
    const otherLocationIdsInSelectedCities = partnerOffering.offeringLocations
      .filter(
        (l) =>
          citiesOfSelectedLocations.includes(l.location.city) &&
          !filter.locationIDs?.includes(l.location.id)
      )
      .map((l) => l.location.id);

    if (otherLocationIdsInSelectedCities.length) {
      return otherLocationIdsInSelectedCities;
    }

    const unselectedLocations = uniqBy(
      partnerOffering.offeringLocations.filter(
        (l) => !filter.locationIDs?.includes(l.location.id)
      ),
      (l) => l.location.id
    );
    const nearbyLocationIds: string[] = [];
    if (filter.locationIDs) {
      for (const selectedLocationId of filter.locationIDs) {
        const selectedLocation = partnerOffering.offeringLocations.find(
          (l) => l.location.id === selectedLocationId
        )?.location;
        for (const otherLocation of unselectedLocations) {
          if (
            selectedLocation?.longitude &&
            selectedLocation.latitude &&
            otherLocation.location.latitude &&
            otherLocation.location.longitude
          ) {
            const distance = calculateDistance(
              {
                latitude: selectedLocation.latitude,
                longitude: selectedLocation.longitude,
              },
              {
                latitude: otherLocation.location.latitude,
                longitude: otherLocation.location.longitude,
              }
            );
            if (distance < NEARBY_LOCATIONS_DISTANCE) {
              nearbyLocationIds.push(otherLocation.location.id);
            }
          }
        }
      }
    }
    return nearbyLocationIds;
  }, [filter.locationIDs, partnerOffering.offeringLocations]);

  const partnerOfferingWithNearbyLocations = useGetPartnerOffering(
    partnerOfferingId,
    {
      ...filter,
      locationIDs: nearbyLocationIds,
    }
  );
  const nearbyLocations = nearbyLocationIds.length
    ? partnerOfferingWithNearbyLocations.offeringLocations
    : [];
  const nearbyLocationSpaces = useSpacesWithLoadingQuery(
    nearbyLocations.map((l) => l.spaceID)
  );

  const loadingSpaces =
    minCapacitySpaces.loading ||
    sameDateSpaces.loading ||
    previousDateSpaces.loading ||
    nextDateSpaces.loading ||
    nearbyLocationSpaces.loading;
  if (loadingSpaces) {
    return <PulseLoader />;
  }

  return (
    <div className="d-flex flex-column" style={{ gap: isMobile ? 40 : 36 }}>
      {(filter.maxCapacity || filter.minCapacity) &&
        !!minCapacitySpaces.data.length && (
          <SimilarRoomsSection
            title={fbt(
              "Similar meeting rooms available for a larger number of guests"
            )}
            spaces={minCapacitySpaces.data}
            sortOrder={sortOrder}
            partnerOffering={partnerOfferingWithMinCapacity}
            partnerOfferingId={partnerOfferingId}
            partnerOfferingLocations={
              partnerOfferingWithMinCapacity.offeringLocations
            }
            filter={filter}
          />
        )}

      {filter.date &&
        filter.startTime &&
        filter.endTime &&
        !!(
          sameDateSpaces.data.length ||
          previousDateSpaces.data.length ||
          nextDateSpaces.data.length
        ) && (
          <SimilarRoomsSection
            title={fbt("Similar meeting rooms available at other times")}
            spaces={
              !!sameDateSpaces.data.length
                ? sameDateSpaces.data
                : previousDateSpaces.data.length
                ? previousDateSpaces.data
                : nextDateSpaces.data
            }
            sortOrder={sortOrder}
            partnerOffering={partnerOffering}
            partnerOfferingId={partnerOfferingId}
            partnerOfferingLocations={
              !!partnerOfferingWitSameDate.offeringLocations.length
                ? partnerOfferingWitSameDate.offeringLocations
                : partnerOfferingWithPreviousDate.offeringLocations.length
                ? partnerOfferingWithPreviousDate.offeringLocations
                : partnerOfferingWithNextDate.offeringLocations
            }
            filter={{
              ...filter,
              date: undefined,
              startTime: undefined,
              endTime: undefined,
              startDate: undefined,
              endDate: undefined,
            }}
          />
        )}

      {filter.locationIDs && !!nearbyLocationSpaces.data.length && (
        <SimilarRoomsSection
          title={fbt("Similar meeting rooms available in nearby locations")}
          spaces={nearbyLocationSpaces.data}
          sortOrder={sortOrder}
          partnerOffering={partnerOfferingWithNearbyLocations}
          partnerOfferingId={partnerOfferingId}
          partnerOfferingLocations={nearbyLocations}
          filter={filter}
        />
      )}
    </div>
  );
}

interface SimilarRoomsSectionProps {
  title: string;
  spaces: Space[];
  sortOrder?: SortOrder;
  partnerOffering: PartnerOffering;
  partnerOfferingId: string;
  partnerOfferingLocations: PartnerOfferingLocation[];
  filter: MeetingRoomsFilter;
}

function SimilarRoomsSection({
  title,
  spaces,
  sortOrder,
  partnerOffering,
  partnerOfferingId,
  partnerOfferingLocations,
  filter,
}: SimilarRoomsSectionProps) {
  const { fbt } = useI18n();
  const { device } = useMediaQuery();
  const isMobile = device === "mobile";

  const [seeAll, setSeeAll] = useState(
    spaces.length > DEFAULT_NUMBER_OF_DISPLAYED_ROOMS ? false : true
  );

  const sortByCapacity = (spaceA: Space, spaceB: Space) => {
    const capacitySpaceA = getMaxCapacity(spaceA);
    const capacitySpaceB = getMaxCapacity(spaceB);
    if (sortOrder === SortOrder.HighestToLowest) {
      return capacitySpaceB - capacitySpaceA;
    }
    return capacitySpaceA - capacitySpaceB;
  };

  return (
    <div>
      <div className="d-flex justify-content-between gap-5">
        <div className="fs-4 fw-bold">{title}</div>
        {!seeAll && !isMobile && (
          <div
            className="text-decoration-underline text-nowrap"
            style={{ color: "var(--bs-secondary)" }}
            role="button"
            onClick={() => setSeeAll(true)}
          >
            {fbt("See all")} ({spaces.length})
          </div>
        )}
      </div>
      <div
        className={classNames(isMobile ? "mt-4" : "mt-3", "row row-cols-md-3")}
      >
        {spaces
          .sort(sortByCapacity)
          .slice(0, seeAll ? undefined : DEFAULT_NUMBER_OF_DISPLAYED_ROOMS)
          .map((space) => (
            <RoomGroup
              key={space.id}
              space={space}
              partnerOffering={partnerOffering}
              partnerOfferingID={partnerOfferingId}
              partnerOfferingLocations={partnerOfferingLocations}
              filter={filter}
            />
          ))}
      </div>
      {!seeAll && isMobile && (
        <Button
          className="mt-4"
          variant="outline bg-white"
          style={{
            borderColor: "var(--bs-secondary)",
            color: "var(--bs-secondary)",
          }}
          onClick={() => setSeeAll(true)}
        >
          {fbt("Show more")}
        </Button>
      )}
    </div>
  );
}
